import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { zodSelectBoxDefault } from "../../../../../../util/zod.util";
import {
  removeUnderscore,
  toTitleCase,
  useFormWrapper,
} from "../../../../../../util/util";
import { SelectBoxV2 } from "../../../../../../components/SelectBoxV2/SelectBoxV2";
import { Form } from "../../../../../../layout/FormLayout";
import type {
  TemplateProductFormStepSchema,
  TemplateProductFormStepItemSchema,
  CollectionSchema,
  CollectionRowSchema,
} from "../../../../../../types/types.PIM";
import {
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../../components/Buttons/Buttons";
import { collectionToOption } from "../AddCollectionToTemplateForm";
import { ToggleSwitchV2 } from "../../../../../../components/ToggleSwitch/ToggleSwitch";
import { TextField } from "../../../../../../components/TextFields/TextFields";
import { TableWithBulkAction } from "../../../../../../components/Table/TableWithBulkAction";
import { TableRefProps } from "../../../../../../components/Table/utils";
import { ColumnDef } from "@tanstack/react-table";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { strings } from "../../../../../../util/strings";

export const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  button {
    display: flex;
  }
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  margin-top: ${({ theme }) => theme.resolve("spacing_4xl")};
`;

const TableWrapper = styled.div`
  div[class*="scroll-table-bulk-action-wrapper"] {
    z-index: 0;
  }
`;

export function CollectionForm({
  onSuccess,
  step,
  templateCollections,
  onCancel,
  collectionToEdit,
}: {
  onSuccess: (
    step: TemplateProductFormStepSchema,
    items: TemplateProductFormStepItemSchema[]
  ) => void;
  step: TemplateProductFormStepSchema;
  templateCollections: CollectionSchema[];
  onCancel: () => void;
  collectionToEdit?: TemplateProductFormStepItemSchema | null;
}) {
  const [selectedCollection, setSelectedCollection] =
    useState<CollectionSchema>();
  const [selectedRows, setSelectedRows] = useState<string[] | "all">([]);

  const [restrictItemsCount, setRestrictItemsCount] = useState(
    collectionToEdit?.restrict_items_count ?? false
  );
  const { t } = useTranslation();
  const resetSelectedRowsRef = useRef<TableRefProps>(null);

  const AddCollectionToTemplateSchema = z.object({
    collection: zodSelectBoxDefault(t),
    restrict_items_count: z.boolean().optional(),
    item_count: restrictItemsCount
      ? z
          .number({
            invalid_type_error: strings(t).thisIsARequiredField,
          })
          .positive(t("Items count must be a positive number"))
          .min(1)
      : z.number().nullable().optional(),
  });

  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(AddCollectionToTemplateSchema),
    defaultValues: {
      collection: {
        label:
          templateCollections.find(
            (collection) =>
              collection.name === collectionToEdit?.group_or_collection
          )?.name ?? "",
        value:
          templateCollections.find(
            (collection) =>
              collection.name === collectionToEdit?.group_or_collection
          )?.id ?? "",
      },
      restrict_items_count: collectionToEdit?.restrict_items_count ?? false,
      item_count: collectionToEdit?.item_count ?? undefined,
    },
  });

  const { handleSubmit, control, formState, errors, register, watch } =
    methodsOfUseForm;

  const itemsCount = watch("item_count") || 0;
  const selectedTemplateCollection = watch("collection");

  const onSubmit = async () => {
    if (selectedCollection) {
      let priority: number;
      if (collectionToEdit) {
        const foundIndex = (step.items || []).findIndex(
          (item) =>
            item.group_or_collection_id ===
            collectionToEdit.group_or_collection_id
        );
        priority =
          foundIndex > -1 ? foundIndex + 1 : (step.items?.length ?? 0) + 1;
      } else {
        priority = (step.items?.length ?? 0) + 1;
      }
      const item = {
        type: "collection_attribute",
        id: crypto.randomUUID(),
        group_or_collection: selectedCollection?.name,
        group_or_collection_id: selectedCollection?.id,
        restrict_items_count: restrictItemsCount,
        item_count: itemsCount,
        is_required: false,
        attributes:
          selectedRows === "all"
            ? selectedCollection?.columns.map((col) => ({
                attribute_id: col.id,
                name: col.name,
                display_name: col.display_name,
                id: col.id,
                is_required:
                  collectionToEdit?.attributes.find(
                    (attr) => attr.name === col.name
                  )?.is_required ?? col.is_required,
                input_type: col.input_type,
                is_multiple_choice: col.is_multiple_choice,
                priority,
              }))
            : selectedRows.map((row) => {
                const col =
                  selectedCollection?.columns[row as unknown as number];
                return {
                  attribute_id: col?.id,
                  name: col?.name,
                  display_name: col?.display_name,
                  id: col?.id,
                  is_required:
                    collectionToEdit?.attributes.find(
                      (attr) => attr.name === col.name
                    )?.is_required ?? col.is_required,
                  input_type: col?.input_type,
                  is_multiple_choice: col?.is_multiple_choice,
                  priority,
                };
              }),
      };

      if (collectionToEdit) {
        const index = step.items?.findIndex(
          (item) =>
            item.group_or_collection_id ===
            collectionToEdit.group_or_collection_id
        );
        if (index !== undefined && index > -1) {
          let newItems = [...(step?.items || [])];
          newItems[index] = item;
          onSuccess(step, newItems);
        }
      } else {
        onSuccess(step, step.items ? [...step.items, item] : [item]);
      }
    }
  };

  const tableColumns = useMemo<ColumnDef<CollectionRowSchema>[]>(
    () => [
      {
        header: t("Attribute Name"),
        id: "attribute_name",
        accessorKey: "attribute_name",
        enableSorting: false,
      },
      {
        header: t("Display Name"),
        accessorKey: "attribute_display_name",
        id: "attribute_display_name",
      },
      {
        header: t("Type"),
        accessorKey: "attribute_type",
        id: "attribute_type",
        cell: (cell) => <span>{toTitleCase(cell.getValue() as string)}</span>,
      },
    ],
    [t]
  );

  const tableData = useMemo<
    {
      attribute_name: string;
      attribute_display_name: string;
      attribute_type: string;
    }[]
  >(
    () =>
      selectedCollection?.columns.map((attr) => ({
        attribute_name: attr.name,
        attribute_display_name: attr.display_name,
        attribute_type: removeUnderscore(attr.input_type),
      })) || [],
    [selectedCollection]
  );

  const handleSelectedRows = (rows: string[] | "all") => {
    setSelectedRows(rows);
  };

  useEffect(() => {
    if (
      selectedTemplateCollection &&
      selectedCollection?.id !== selectedTemplateCollection.value
    ) {
      setSelectedCollection(
        templateCollections.find(
          (collection) => collection.name === selectedTemplateCollection.label
        )
      );
      setSelectedRows([]);
    }
  }, [selectedCollection?.id, selectedTemplateCollection, templateCollections]);

  useEffect(() => {
    if (collectionToEdit) {
      setSelectedCollection(
        templateCollections.find(
          (collection) =>
            collection.name === collectionToEdit?.group_or_collection
        )
      );
      setSelectedRows(
        collectionToEdit.attributes.reduce((acc, attr) => {
          const collection = templateCollections.find(
            (collection) =>
              collection.name === collectionToEdit?.group_or_collection
          );
          const index = collection?.columns.findIndex(
            (col) => col.id === attr.attribute_id
          );
          if (index !== undefined && index !== -1) {
            acc.push(index.toString());
          }
          return acc;
        }, [] as string[])
      );
    }
  }, [collectionToEdit, templateCollections]);

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <>
          <Controller
            as={SelectBoxV2}
            control={control}
            name={`collection`}
            defaultValue={{
              label:
                templateCollections.find(
                  (collection) =>
                    collection.name === collectionToEdit?.group_or_collection
                )?.name ?? "",
              value:
                templateCollections.find(
                  (collection) =>
                    collection.name === collectionToEdit?.group_or_collection
                )?.id ?? "",
            }}
            placeholder={t("Collection Name")}
            isDisabled={!!collectionToEdit}
            options={templateCollections
              .map(collectionToOption)
              .filter(
                (option) =>
                  !step.items?.some(
                    (item) => item.group_or_collection === option.label
                  )
              )}
            rules={{ required: true }}
            errors={errors}
            formState={formState}
            style={{ zIndex: "5" }}
          />
          {selectedCollection && (
            <TableWrapper>
              <TableWithBulkAction
                columns={tableColumns}
                data={tableData}
                isLoading={false}
                error={undefined}
                handleSelectRows={(rows) => handleSelectedRows(rows)}
                defaultSelectedRows={selectedRows}
                bulkActionElement={() => <></>}
                ref={resetSelectedRowsRef}
                maxHeight="100%"
                bulkSelectLimit={500}
                enableColumnResize={false}
              />
            </TableWrapper>
          )}
          <ToggleSwitchV2
            label={t("Restrict number of items that can be added")}
            name="restrict_items_count"
            checked={restrictItemsCount}
            onChange={(e) => setRestrictItemsCount(e.target.checked)}
          />
          {restrictItemsCount && (
            <TextField
              label={t("Number of items")}
              name="item_count"
              theref={register({
                required: restrictItemsCount,
                valueAsNumber: true,
              })}
              errors={errors}
              formState={formState}
              type="number"
            />
          )}
        </>
        <ButtonGroup>
          <TertiaryButtonLargeV2 fullWidth type="button" onClick={onCancel}>
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2
            fullWidth
            type="submit"
            disabled={selectedRows.length === 0}
            data-tooltip-id="tooltip-id"
            data-tip={
              selectedRows.length === 0
                ? t("Please select at least one attribute")
                : ""
            }
            data-for="tooltip-id"
          >
            {t("Save")}
          </PrimaryButtonLargeV2>
          <ReactTooltip id="tooltip-id" effect="solid" />
        </ButtonGroup>
      </Form>
    </>
  );
}
